import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { I_IQPlayerGallery, I_IQPlayerGallerySettings } from '../interfaces';
import StopAudioIcon from '../../../../../ui/svg/audio/stop-audio';
import PlayAudioIcon from '../../../../../ui/svg/audio/play-audio';
import PillToggle from '../../../../../ui/toggle/pill-toggle/pill-toggle';
import { IPillToggleOption } from '../../../../../ui/toggle/pill-toggle/pill-toggle-interfaces';
import { sendGAEvent } from '../../../../../domain/marketing/gtm-provider';

export const STOP_PLAY_OTHER_EVENT = 'STOP_PLAY_OTHER_EVENT';

interface IIqpsAudioProps {
    item: I_IQPlayerGallery;
    iQPlayerGallerySettings: I_IQPlayerGallerySettings;
    runID: string | number;
}

const selectedIndex = 0;

const toggleOption1: IPillToggleOption = {
    text: 'Original',
    value: 'Original',
};

const toggleOption2: IPillToggleOption = {
    text: 'Mastered',
    value: 'Mastered',
};

const IqpsAudio = ({ item, runID }: IIqpsAudioProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    // const [isReGen, setisReGen] = useState(false);
    const [selectedToggleOption, setSelectedToggleOption] = useState<IPillToggleOption>(toggleOption1);
    const isReGen = selectedToggleOption.value !== toggleOption1.value;

    const audioCtxRef = useRef<AudioContext | null>(null);
    const gainNodesRef = useRef<GainNode[]>([]);
    const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
    const buffersRef = useRef<AudioBuffer[]>([]);

    const bypassIndex = useMemo(
        () => Math.max(0, item?.audioSrc?.length - 1),
        [item?.audioSrc]
    );

    const ensureContext = () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
        }
        return audioCtxRef.current!;
    };

    const teardownSources = () => {
        sourcesRef.current.forEach((src) => {
            try { src.stop(0); } catch {}
            try { src.disconnect(); } catch {}
        });
        sourcesRef.current = [];
    };

    const createGainsIfNeeded = (count: number) => {
        const ctx = ensureContext();
        if (gainNodesRef.current.length === count) return;
        gainNodesRef.current.forEach((g) => { try { g.disconnect(); } catch {} });
        gainNodesRef.current = new Array(count).fill(null).map(() => {
            const g = ctx.createGain();
            g.gain.value = 0;
            g.connect(ctx.destination);
            return g;
        });
    };

    const loadAllBuffers = async () => {
        const ctx = ensureContext();
        const urls = item?.audioSrc || [];
        if (!urls.length) return;
        createGainsIfNeeded(urls.length);
        const arrayBuffers = await Promise.all(
            urls.map(async (url) => {
                const res = await fetch(url);
                return await res.arrayBuffer();
            })
        );
        const decoded = await Promise.all(arrayBuffers.map((ab) => ctx.decodeAudioData(ab.slice(0))));
        buffersRef.current = decoded;
    };

    const startPlayback = async () => {
        const ctx = ensureContext();
        if (ctx.state === "suspended") await ctx.resume();

        document.dispatchEvent(
            new CustomEvent(STOP_PLAY_OTHER_EVENT, { detail: { id: runID } })
        );

        teardownSources();
        createGainsIfNeeded(item.audioSrc.length);

        sourcesRef.current = buffersRef.current.map((buf, i) => {
            const src = ctx.createBufferSource();
            src.buffer = buf;
            src.loop = true;
            src.connect(gainNodesRef.current[i]);
            return src;
        });

        const activeIndex = isReGen ? selectedIndex : bypassIndex;
        gainNodesRef.current.forEach((g, i) => (g.gain.value = i === activeIndex ? 1 : 0));
        sourcesRef.current.forEach((s) => s.start());

        setIsPlaying(true);

        sendGAEvent(`Player ${ item?.title }`, 'Play');
    };

    const stopPlayback = useCallback(() => {
        teardownSources();
        setIsPlaying(false);
    }, []);

    const switchActiveTrack = (activeIndex: number) => {
        gainNodesRef.current.forEach((g, i) => (g.gain.value = i === activeIndex ? 1 : 0));
    };

    // auto-load Buffers
    useEffect(() => {
        //console.log('item: ',item);
        loadAllBuffers();
        return () => {
            stopPlayback();
            try { audioCtxRef.current?.close(); } catch {}
            audioCtxRef.current = null;
            gainNodesRef.current = [];
            buffersRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // change StudioVerse
    useEffect(() => {
        if (!isPlaying) return;
        const idx = isReGen ? selectedIndex : bypassIndex;
        switchActiveTrack(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReGen]);

    // change selected track
    useEffect(() => {
        if (!isPlaying || !isReGen) return;
        switchActiveTrack(selectedIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    useEffect(() => {
        const onStopOther = (e: Event) => {
            const ce = e as CustomEvent<{ id: string | number }>;
            // если событие пришло не от нас — остановиться
            if (ce?.detail?.id !== runID) {
                if (isPlaying) stopPlayback();
            }
        };
        document.addEventListener(STOP_PLAY_OTHER_EVENT, onStopOther as EventListener);
        return () => {
            document.removeEventListener(STOP_PLAY_OTHER_EVENT, onStopOther as EventListener);
        };
    }, [
        runID,
        isPlaying,
        stopPlayback,
    ]);

    /*const getCurrentTrackName = () => {
        if (!isPlaying) return "";
        const idx = isReGen ? selectedIndex : bypassIndex;
        return item?.audioSrc?.[idx] ? item.audioSrc[idx].split("/").pop() || "" : "";
    };*/

    return (
        <div className="rounded-3xl overflow-hidden olm-audio-player-item">
            <div className={ `pt-6 pb-6 px-4` }>

                <div className="font-semibold text-xl md:text-2xl text-left pb-4">{ item?.title }</div>

                <div className="flex flex-row md:items-center justify-between gap-4">
                    <button
                        className="olm-audio-player-item__audio_btn"
                        type="button"
                        aria-label={ `${ isPlaying ? 'Stop' : 'Play' } for ${ item?.title }` }
                        aria-pressed={ isPlaying }
                        onClick={() => {
                            isPlaying ? stopPlayback() : startPlayback();
                        }}>
                        {
                            isPlaying ?
                                <StopAudioIcon
                                    width="40"
                                    height="40"
                                    color={ item?.colorEx1 }
                                    bgColor={ '#3C3C3F' }
                                /> :

                                <PlayAudioIcon
                                    width="40"
                                    height="40"
                                    color={ item?.colorEx1 }
                                    bgColor={ '#3C3C3F' }
                                />
                        }
                    </button>

                    <PillToggle
                        option1={ toggleOption1 }
                        option2={ toggleOption2 }
                        selectedOption={ selectedToggleOption }
                        onChange={ (option: IPillToggleOption) => {
                            setSelectedToggleOption(option);
                            sendGAEvent(`Player ${ item?.title }`, `Toggle ${ option.text }`);
                        }}
                        ariaLabel={ 'Select an option' }
                    />

                    {/*<label className="inline-flex items-center gap-2 cursor-pointer select-none text1">
                        <span className={`text-sm ${ isReGen ? '' : 'font-bold'}`}>Original</span>
                        <Toggle
                            isChecked={isReGen}
                            handleColor={isReGen ? item?.colorEx1 : item?.bgColor}
                            bgClass="bg-black"
                            borderClass="border border-gray-800"
                            onChange={(checked: boolean) => setisReGen(checked)}
                            width={ 50 }
                            // text={isReGen ? 'On' : 'Off'}
                            isPositionRight={ false }
                            textStyles={{
                                marginLeft: '-5px',
                            }}
                        />
                        <span className={`text-sm ${ isReGen ? 'font-bold' : '' }`}>ReGen</span>
                    </label>*/}
                </div>
            </div>

            {
                item?.img &&
                <img
                    className="bg-gray-400 w-full object-cover"
                    src={ item.img }
                    alt={ item.title }
                />
            }
        </div>
    );
};

export default IqpsAudio;
